import { CoachingConfig } from "@callveriq/type/coaching";
import { MeetingTypeConst } from "@callveriq/type/Common";
import {
    Banner,
    DashboardKeys,
    DomainConfigDashboard,
    DomainConfigData,
    DomainConfigSetting,
    FeatureAccess,
    FeatureAccessKeys,
    SettingKeys,
    StatsThreshold,
    TenantFilter,
} from "@callveriq/type/Domain";
import { capitalizeFirstLetter } from "@callveriq/utils/helper/common.helper";

class DomainInstance {
    #domainConfig: DomainConfigData;
    readonly logo: string | null;
    readonly brand: string | null;
    readonly hasMultipleConversationTypes: boolean;
    readonly statsThreshold: StatsThreshold;
    readonly domainType: "b2b" | "b2c";
    readonly version: number;
    readonly autoDeductEnabled: boolean;
    readonly isMeetingsDefault: boolean;
    readonly battle_card_trigger_medium: string | null;

    constructor(domainConfig: DomainConfigData) {
        this.#domainConfig = domainConfig;
        this.hasMultipleConversationTypes =
            domainConfig.enabled_conversation_types?.length > 1;
        this.statsThreshold = domainConfig.tenant_filter
            ?.conversation_stats_threshold ?? {
            good: 75,
            bad: 50,
            average: 50,
        };
        this.logo = domainConfig.logo;
        this.brand = domainConfig.brand;
        this.domainType = domainConfig.tenant_type;
        this.version = domainConfig.version;
        this.autoDeductEnabled = domainConfig.auto_deduct_enabled ?? false;
        this.isMeetingsDefault = domainConfig.isMeetingsDefault ?? false;
        this.battle_card_trigger_medium =
            domainConfig.battle_card_trigger_medium ?? null;
    }

    getFeatureAccess(
        featureName: FeatureAccessKeys
    ): FeatureAccess | undefined {
        return this.#domainConfig.feature_accesses?.find(
            (feature) => feature.feature_name === featureName
        );
    }

    isFeatureVisible(featureName: FeatureAccessKeys): boolean {
        return this.getFeatureAccess(featureName)?.visibility ?? false;
    }

    isFeatureAccessible(featureName: FeatureAccessKeys): boolean {
        return this.getFeatureAccess(featureName)?.accessibility ?? false;
    }

    getDashboard(
        dashboardType: DashboardKeys
    ): DomainConfigDashboard | undefined {
        return this.#domainConfig.dashboards?.find(
            (dashboard) => dashboard.dashboard_type === dashboardType
        );
    }

    getSetting(settingType: SettingKeys): DomainConfigSetting | undefined {
        return this.#domainConfig.settings.find(
            (setting) => setting.setting_type === settingType
        );
    }

    isDashboardActive(dashboardType: DashboardKeys): boolean {
        return this.getDashboard(dashboardType)?.is_active ?? false;
    }

    isSettingVisible(settingType: SettingKeys): boolean {
        return this.getSetting(settingType)?.is_active ?? false;
    }

    getEnabledConversationTypes(): MeetingTypeConst[] {
        return this.#domainConfig.enabled_conversation_types;
    }

    hasRuleEngine(): boolean {
        return this.#domainConfig.audit_config?.audit_type.includes(
            "rule_engine"
        );
    }

    hasGptAudit(): boolean {
        return this.#domainConfig.audit_config?.audit_type.includes(
            "gpt_audit"
        );
    }

    getBanners(): Banner[] | undefined {
        return this.#domainConfig.domain_banners;
    }

    getSaleAssistUrl(): string | undefined {
        return this.getFeatureAccess("sale_assist")?.metadata
            ?.sales_agent_url as string | undefined;
    }

    getConversationTypeOptions(): Array<{
        id: NonNullable<MeetingTypeConst>;
        label: string;
    }> {
        return this.#domainConfig.enabled_conversation_types
            .map((type) => ({
                id: type,
                label: capitalizeFirstLetter(type ?? ""),
                value: capitalizeFirstLetter(type ?? ""),
            }))
            .filter((type) => type.id !== null) as Array<{
            id: NonNullable<MeetingTypeConst>;
            label: string;
        }>;
    }

    getAiAgentHostUrl(): string | undefined {
        return this.getFeatureAccess("ai_agent")?.metadata?.ai_agent_host as
            | string
            | undefined;
    }

    getDefaultFilters(): TenantFilter {
        const obj = {} as Record<keyof TenantFilter, unknown>;
        const filters = this.#domainConfig.tenant_filter;
        Object.keys(filters ?? {}).forEach((key) => {
            const typedKey = key as keyof TenantFilter;
            if (
                typeof filters[typedKey] === "string" &&
                filters[typedKey] === "unspecified"
            ) {
                obj[typedKey] = null;
            } else {
                obj[typedKey] = filters[typedKey] || null;
            }
        });
        return obj as TenantFilter;
    }

    getSsoProviders(): string[] {
        return (
            this.#domainConfig.sso_providers?.map((provider) =>
                provider.toLowerCase()
            ) ?? []
        );
    }

    isLmsTranscriptionEnabled(): boolean {
        return this.#domainConfig.lms_config?.transcription ?? false;
    }

    isLmsAiFeatureEnabled(): boolean {
        return this.#domainConfig.lms_config?.ai_feature ?? false;
    }

    isAssessmentAiEvaluationEnabled(): boolean {
        return this.#domainConfig.assessment_config?.ai_evaluation ?? false;
    }

    getSupersetUrl(): string | undefined {
        return this.getDashboard("superset")?.metadata?.superset_url as
            | string
            | undefined;
    }

    /**
     * If ciMethodEnabled is true, then show ai insights data in callveriq insights tab
     * @returns boolean
     */
    isCiMethodEnabled(): boolean {
        return (
            this.#domainConfig.dashboards.find(
                (dashboard) => dashboard.dashboard_type === "ci_gpt_method"
            )?.is_active ?? false
        );
    }

    getAiDispositionTemplateId(): number | undefined {
        return this.getFeatureAccess("ai_disposition")?.metadata
            ?.ai_disposition_template as number | undefined;
    }

    isMultipleDispositionEnabled(): boolean {
        return (
            Boolean(
                this.getFeatureAccess("ai_disposition")?.metadata
                    ?.multiple_dispositions
            ) ?? false
        );
    }

    getDomainConfig() {
        return this.#domainConfig;
    }

    getCoachingConfig(): CoachingConfig {
        return this.#domainConfig.coaching;
    }
}

export default DomainInstance;
